import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trophy, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { useSales } from '@/hooks/useSales';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { useCompany } from '@/context/CompanyContext';
import { PeriodFilterType } from '@/components/common/PeriodFilter';

interface BestSellersTableProps {
  periodFilter: PeriodFilterType;
}

export const BestSellersTable: React.FC<BestSellersTableProps> = ({ periodFilter }) => {
  const { sales, loading: salesLoading } = useSales();
  const { products, loading: productsLoading } = useSupabaseInventory();
  const { userRole } = useCompany();
  const [showAll, setShowAll] = useState(false);

  const filteredSales = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return sales.filter(sale => {
      const saleDate = new Date(sale.sale_date);
      switch (periodFilter) {
        case 'today':
          return saleDate >= today;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return saleDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return saleDate >= monthAgo;
        default:
          return true;
      }
    });
  }, [sales, periodFilter]);

  const bestSellers = useMemo(() => {
    const productStats = filteredSales.reduce((acc, sale) => {
      const key = sale.product_id;
      if (!acc[key]) {
        acc[key] = {
          productId: sale.product_id,
          productName: sale.product_name,
          totalSold: 0,
          revenue: 0
        };
      }
      acc[key].totalSold += sale.quantity_sold;
      acc[key].revenue += sale.total_amount;
      return acc;
    }, {} as Record<string, any>);

    return Object.values(productStats)
      .sort((a: any, b: any) => b.totalSold - a.totalSold)
      .slice(0, 10)
      .map((item: any, index) => ({
        ...item,
        rank: index + 1,
        currentStock: products.find(p => p.id === item.productId)?.quantity || 0
      }));
  }, [filteredSales, products]);

  // Données à afficher selon l'état showAll
  const displayedSellers = showAll ? bestSellers : bestSellers.slice(0, 3);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getRankBadgeVariant = (rank: number) => {
    if (rank === 1) return 'default';
    if (rank <= 3) return 'secondary';
    return 'outline';
  };

  const getRankIcon = (rank: number) => {
    if (rank <= 3) return <Trophy className="w-4 h-4" />;
    return <Package className="w-4 h-4" />;
  };

  if (salesLoading || productsLoading) {
    return (
      <Card>
        <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-gradient-to-br from-amber-500/10 to-amber-600/20 transition-transform duration-200 hover:scale-105">
            <Trophy className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium mb-1 text-amber-700 dark:text-amber-300">
              Produits populaires
            </p>
            <p className="text-xl font-bold text-amber-800 dark:text-amber-200">
              Meilleures ventes
            </p>
          </div>
        </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-muted rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col max-h-[800px]">
      <CardHeader className="flex-none sticky top-0 z-10 bg-background border-b">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-gradient-to-br from-amber-500/10 to-amber-600/20 transition-transform duration-200 hover:scale-105">
            <Trophy className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium mb-1 text-amber-700 dark:text-amber-300">
              Produits populaires
            </p>
            <p className="text-xl font-bold text-amber-800 dark:text-amber-200">
              Meilleures ventes
            </p>
          </div>
          <Badge variant="outline" className="ml-auto">
            Top {showAll ? '10' : '3'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        {bestSellers.length === 0 ? (
          <>
            <div className="sr-only" aria-live="polite">
              Aucune vente pour la période sélectionnée
            </div>
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                Aucune vente pour cette période
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="sr-only" aria-live="polite">
              Tableau des meilleures ventes. {bestSellers.length} produits. Affichage {showAll ? 'complet' : 'des 3 premiers'}.
              {displayedSellers.map((item) => 
                `Position ${item.rank}: ${item.productName}, ${item.totalSold} unités vendues${userRole === 'admin' ? `, chiffre d'affaires ${formatPrice(item.revenue)}` : ''}, stock restant ${item.currentStock} unités`
              ).join('. ')}
            </div>
            <Table aria-label="Meilleures ventes" role="table">
              <caption className="sr-only">
                Top {showAll ? '10' : '3'} des meilleures ventes. Ce tableau affiche le rang, le nom du produit, la quantité vendue, 
                {userRole === 'admin' ? 'le chiffre d\'affaires généré, ' : ''}et le stock restant.
              </caption>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16" scope="col">Rang</TableHead>
                  <TableHead scope="col">Produit</TableHead>
                  <TableHead className="text-center" scope="col">Quantité vendue</TableHead>
                  {userRole === 'admin' && (
                    <TableHead className="text-right" scope="col">CA généré</TableHead>
                  )}
                  <TableHead className="text-center" scope="col">Stock restant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedSellers.map((item) => (
                  <TableRow key={item.productId} className="hover:bg-muted/50">
                    <TableCell scope="row">
                      <Badge variant={getRankBadgeVariant(item.rank)} className="flex items-center gap-1">
                        {getRankIcon(item.rank)}
                        {item.rank}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {item.productName}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">
                        {item.totalSold} unités
                      </Badge>
                    </TableCell>
                    {userRole === 'admin' && (
                      <TableCell className="text-right font-medium">
                        {formatPrice(item.revenue)}
                      </TableCell>
                    )}
                    <TableCell className="text-center">
                      <Badge 
                        variant={item.currentStock > 0 ? "secondary" : "destructive"}
                      >
                        {item.currentStock}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Bouton pour voir plus/moins */}
            {bestSellers.length > 3 && (
              <div className="flex justify-center mt-4 pt-4 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAll(!showAll)}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  {showAll ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Voir moins
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Voir plus ({bestSellers.length - 3} autres)
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};